import { ReportScheduleRecurrence as Recurrence } from '@/__generated__/graphql';
import { useFormatters } from '@/hooks/useFormatters';
import { capitalizeFirstLetter } from '@/utils/stringUtils';
import ApolloErrorBoundary from '@components/ApolloErrorBoundary';
import { AutocompleteController } from '@components/controllers/AutocompleteController';
import { FormControlLabelController } from '@components/controllers/FormControlLabelController';
import { TimePickerController } from '@components/controllers/TimePickerController';
import { StepProps } from '@features/reports2/types';
import {
  getCronExpression,
  getDayOfWeekOptions,
  getHoursAndMinutes,
  getNextCronExecutions,
  getRecurrenceOptions,
} from '@features/reports2/utils/schedule';
import { msg, Trans } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { Box, Checkbox, Divider, Typography } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers-latest';
import { AdapterDayjs } from '@mui/x-date-pickers-latest/AdapterDayjs';
import { FC, ReactNode, Suspense, useEffect, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { CreateReportFieldValues } from '../../validation';
import { InnerPaper, StepContentBox, StepTitle } from '../common';

const InputDistribution: FC<{
  recurrence: Recurrence;
  timeInput: ReactNode;
  daysOfWeekInput: ReactNode;
  dayOfWeekInput: ReactNode;
}> = ({ recurrence, timeInput, daysOfWeekInput, dayOfWeekInput }) => {
  if (recurrence === Recurrence.Daily) return <Trans>Ejecutar diariamente a las {timeInput}</Trans>;
  if (recurrence === Recurrence.Weekdays)
    return <Trans>Ejecutar los días de la semana (de lunes a viernes) a las {timeInput}</Trans>;
  if (recurrence === Recurrence.Weekly)
    return (
      <Trans>
        Ejecutar semanalmente los días {daysOfWeekInput} a las {timeInput}
      </Trans>
    );
  if (recurrence === Recurrence.MonthlyFirstDay)
    return (
      <Trans>
        Ejecutar en el primer {dayOfWeekInput} del mes a las {timeInput}
      </Trans>
    );
  if (recurrence === Recurrence.MonthlyLastDay)
    return (
      <Trans>
        Ejecutar en el último {dayOfWeekInput} del mes a las {timeInput}
      </Trans>
    );
  return null;
};

const SuspenseComponent: FC<StepProps> = (props) => {
  const { _, i18n } = useLingui();
  const { trigger, ...methods } = useFormContext<CreateReportFieldValues>();
  const formatters = useFormatters();

  const enabled = methods.watch('fourthStep.enabled');

  const recurrenceOptions = getRecurrenceOptions(i18n);
  const dayOfWeekOptions = getDayOfWeekOptions(i18n);

  const errors = methods.formState.errors.fourthStep;
  const nextStepEnabled =
    !errors?.schedule?.time &&
    !errors?.schedule?.dayOfWeek &&
    !errors?.schedule?.daysOfWeek &&
    !errors?.schedule?.recurrence;

  const recurrence = methods.watch('fourthStep.schedule.recurrence');

  useEffect(() => {
    trigger('fourthStep');
  }, [recurrence, trigger, enabled]);

  const schedule = methods.watch('fourthStep.schedule');

  const nextCronExpressions = useMemo(() => {
    if (!schedule) return [];
    const { hours, minutes } = getHoursAndMinutes(schedule?.time);

    const cronExpression = getCronExpression(schedule.recurrence, {
      daysOfWeek: schedule.daysOfWeek ?? undefined,
      dayOfWeek: schedule.dayOfWeek ?? undefined,
      minutes,
      hours,
    });

    if (!cronExpression) return [];

    return getNextCronExecutions(cronExpression, formatters);
  }, [schedule, formatters]);

  return (
    <StepContentBox
      actions={{
        ...props.actions,
        nextStepEnabled: props.actions.nextStepEnabled && nextStepEnabled,
        onNextStep: () => {
          trigger('fourthStep.schedule').then((success) => {
            if (success) {
              props.actions.onNextStep();
            }
          });
        },
      }}
    >
      <StepTitle
        title={<Trans>Programar (opcional)</Trans>}
        description={
          <Trans>
            Activa y configura la recurrencia para el envío automático de este reporte. Si no la
            activas, el reporte se generará solo esta vez.
          </Trans>
        }
      />

      <Box p={1}>
        <FormControlLabelController
          control={methods.control}
          name="fourthStep.enabled"
          inputControl={<Checkbox />}
          label={_(msg`Programar este reporte para ejecutar periódicamente`)}
        />
      </Box>
      {enabled && (
        <InnerPaper mt={4}>
          <Box mx="auto" maxWidth={740}>
            <Typography fontWeight="bold" mb={2}>
              <Trans>Especifica la recurrencia de envío</Trans>
            </Typography>
            <Box display="flex" alignItems="center" gap={1}>
              <Typography>
                <Trans>Elige la recurrencia en la cual deseas recibir el reporte</Trans>:{' '}
              </Typography>
              <AutocompleteController
                label={_(msg`Recurrencia`)}
                control={methods.control}
                name="fourthStep.schedule.recurrence"
                options={recurrenceOptions.map((option) => option.value)}
                slotProps={{
                  autocomplete: {
                    getOptionLabel(value) {
                      const option = recurrenceOptions.find((option) => option.value === value);
                      return option?.label ?? value;
                    },
                    size: 'small',
                    sx: {
                      width: 300,
                    },
                  },
                }}
              />
            </Box>
            <Divider sx={{ my: 2 }} />

            <Box mb={2}>
              <Typography display="flex" alignItems="center" gap={1} flexWrap="wrap">
                <InputDistribution
                  key={methods.watch('fourthStep.schedule.recurrence')}
                  recurrence={methods.watch('fourthStep.schedule.recurrence')}
                  timeInput={
                    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={i18n.locale}>
                      <TimePickerController
                        control={methods.control}
                        name="fourthStep.schedule.time"
                        slotProps={{
                          textField: {
                            size: 'small',
                            sx: { width: 150 },
                            helperText: '',
                          },
                        }}
                      />
                    </LocalizationProvider>
                  }
                  dayOfWeekInput={
                    <AutocompleteController
                      label={_(msg`Día de la semana`)}
                      control={methods.control}
                      name="fourthStep.schedule.dayOfWeek"
                      options={dayOfWeekOptions.map((option) => option.enumValue)}
                      slotProps={{
                        textField: {
                          helperText: '',
                        },
                        autocomplete: {
                          getOptionLabel(value) {
                            const option = dayOfWeekOptions.find(
                              (option) => option.enumValue === value,
                            );
                            return option?.label ?? value;
                          },
                          size: 'small',
                          sx: { width: 240 },
                        },
                      }}
                    />
                  }
                  daysOfWeekInput={
                    <AutocompleteController
                      label={_(msg`Días de la semana`)}
                      control={methods.control}
                      name="fourthStep.schedule.daysOfWeek"
                      defaultValue={[]}
                      options={dayOfWeekOptions.map((option) => option.enumValue)}
                      slotProps={{
                        autocomplete: {
                          multiple: true,
                          getOptionLabel(value) {
                            const option = dayOfWeekOptions.find(
                              (option) => option.enumValue === value,
                            );
                            return option?.label ?? value;
                          },
                          size: 'small',
                          sx: { width: 300 },
                        },
                      }}
                    />
                  }
                />
              </Typography>
            </Box>

            {nextCronExpressions.length > 0 && (
              <Box border={1} borderRadius={1} p={1.5}>
                <Typography mb={1}>
                  <Trans>
                    Con esta configuración los próximos reportes se generarían en las siguientes
                    fechas
                  </Trans>
                  :
                </Typography>
                {nextCronExpressions.map((execution) => (
                  <Typography fontStyle="italic" id={execution}>
                    {capitalizeFirstLetter(execution)}
                  </Typography>
                ))}
              </Box>
            )}
          </Box>
        </InnerPaper>
      )}
    </StepContentBox>
  );
};

export const ScheduleStep: FC<StepProps> = (props) => {
  return (
    <ApolloErrorBoundary>
      <Suspense>
        <SuspenseComponent {...props} />
      </Suspense>
    </ApolloErrorBoundary>
  );
};
